import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  BasePolicy,
  Branch,
  Guarantee,
  PolicySubject,
  PolicySubjectRoleAction,
  ReadOnlyPolicy,
  useAddPolicyGuaranteeMutation,
  useCreatePolicyMutation,
  useDeletePolicyGuaranteeMutation,
  useLazyGetBranchesListQuery,
  useLazyGetReadOnlyPolicyQuery,
  useSetRolesToPolicySubjectsMutation,
  useUpdatesPolicyGuaranteeMutation,
} from "../redux/apiSpecifications/apiPolicies";

export const usePolicy = () => {
  const [getReadOnlyPolicy] = useLazyGetReadOnlyPolicyQuery();
  const [getBranchesList] = useLazyGetBranchesListQuery();

  const [addPolicyGuarantee] = useAddPolicyGuaranteeMutation();
  const [updatesPolicyGuarantee] = useUpdatesPolicyGuaranteeMutation();
  const [deletePolicyGuarantee] = useDeletePolicyGuaranteeMutation();

  const [createPolicy] = useCreatePolicyMutation();

  const [setRolesToPolicySubjects] = useSetRolesToPolicySubjectsMutation();

  const getPolicyPromise = (id: number): Promise<ReadOnlyPolicy | undefined> =>
    new Promise(async (resolve, reject) => {
      const response = await getReadOnlyPolicy({
        id: id,
      });

      const getResponse = response as {
        data: ReadOnlyPolicy;
      };

      if (getResponse.data) {
        resolve(getResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const getBranchesListPromise = (): Promise<Branch[]> =>
    new Promise(async (resolve, reject) => {
      const response = await getBranchesList();

      const getResponse = response as {
        data: Branch[];
      };

      if (getResponse.data) {
        resolve(getResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const addPolicyGuaranteePromise = (
    policyId: number,
    guarantee: Guarantee
  ): Promise<Guarantee[]> =>
    new Promise(async (resolve, reject) => {
      const response = await addPolicyGuarantee({
        id: policyId,
        guarantee: guarantee,
      });

      const postResponse = response as {
        data: Guarantee[];
      };

      if (postResponse.data) {
        resolve(postResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const updatePolicyGuaranteePromise = (
    policyId: number,
    guarantee: Guarantee
  ): Promise<Guarantee[]> =>
    new Promise(async (resolve, reject) => {
      const response = await updatesPolicyGuarantee({
        policyId,
        guaranteeId: guarantee.id!,
        guarantee,
      });

      const putResponse = response as {
        data: Guarantee[];
      };

      if (putResponse.data) {
        resolve(putResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const deletePolicyGuaranteePromise = (
    policyId: number,
    guaranteeId: number
  ): Promise<Guarantee[]> =>
    new Promise(async (resolve, reject) => {
      const response = await deletePolicyGuarantee({
        policyId,
        guaranteeId,
      });

      const deleteResponse = response as {
        data: Guarantee[];
      };

      if (deleteResponse.data) {
        resolve(deleteResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const createPolicyPromise = (
    basePolicy: BasePolicy
  ): Promise<ReadOnlyPolicy> =>
    new Promise(async (resolve, reject) => {
      const response = await createPolicy({
        basePolicy,
      });

      const postResponse = response as {
        data: ReadOnlyPolicy;
      };

      if (postResponse.data) {
        resolve(postResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  const setRolesToPolicySubjectsPromise = (
    policyId: number,
    policySubjectRoleActions: PolicySubjectRoleAction[]
  ): Promise<PolicySubject[]> =>
    new Promise(async (resolve, reject) => {
      const response = await setRolesToPolicySubjects({
        id: policyId,
        body: policySubjectRoleActions,
      });

      const postResponse = response as {
        data: PolicySubject[];
      };

      if (postResponse.data) {
        resolve(postResponse.data);
      } else {
        const errorResponse = response as {
          error: FetchBaseQueryError;
        };

        reject(errorResponse.error);
      }
    });

  return {
    getPolicyPromise,
    getBranchesListPromise,
    addPolicyGuaranteePromise,
    updatePolicyGuaranteePromise,
    deletePolicyGuaranteePromise,
    createPolicyPromise,
    setRolesToPolicySubjectsPromise,
  };
};
